/**
 * navigation.ts – Recipes para shell de la app: sidebar y header.
 *
 * Propósito: centralizar los estilos del sidebar de navegación lateral
 * y del header/appbar, eliminando useTheme() inline de esos componentes.
 */

// ─── Sidebar ────────────────────────────────────────────────────────────────

/** Contenedor raíz del sidebar */
export const sidebarContainerSx = {
  width: 280,
  height: '100vh',
  backgroundColor: 'background.paper',
  borderRight: '1px solid',
  borderColor: 'divider',
  display: 'flex',
  flexDirection: 'column' as const,
  overflow: 'hidden',
};

/** Cabecera del sidebar (fondo primary, logo + nombre app) */
export const sidebarHeaderSx = {
  px: 3,
  height: 64,
  display: 'flex',
  alignItems: 'center',
  overflow: 'hidden',
  borderBottom: '1px solid',
  borderColor: 'divider',
  backgroundColor: 'primary.main',
  color: 'primary.contrastText',
  flexShrink: 0,
};

/** Área de logo dentro del header del sidebar */
export const sidebarLogoIconSx = {
  width: 40,
  height: 40,
  mr: 2,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

/** Contenedor del logo PNG en el header del sidebar */
export const sidebarLogoWrapperSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
  overflow: 'hidden',
};

/** Nav item activo */
export const sidebarNavItemActiveSx = {
  borderRadius: 2,
  mx: 1,
  backgroundColor: 'primary.main',
  color: 'primary.contrastText',
  '&:hover': {
    backgroundColor: 'primary.dark',
  },
  '& .MuiListItemIcon-root': {
    color: 'primary.contrastText',
  },
};

/** Nav item inactivo */
export const sidebarNavItemSx = {
  borderRadius: 2,
  mx: 1,
  backgroundColor: 'transparent',
  color: 'text.primary',
  '&:hover': {
    backgroundColor: 'action.hover',
  },
  '& .MuiListItemIcon-root': {
    color: 'text.secondary',
  },
};

/** Badge numérico en nav item */
export const sidebarNavBadgeSx = {
  backgroundColor: 'error.main',
  color: 'error.contrastText',
  borderRadius: '50%',
  width: 20,
  height: 20,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.75rem',
  fontWeight: 600,
};

// ─── Header / AppBar ────────────────────────────────────────────────────────

/** AppBar principal */
export const appBarSx = {
  backgroundColor: 'background.paper',
  borderBottom: '1px solid',
  borderColor: 'divider',
  color: 'text.primary',
  width: '100%',
  left: 0,
  right: 0,
};

/** Toolbar dentro del AppBar */
export const toolbarSx = {
  px: { xs: 2, md: 3 },
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
};

/** Caja de búsqueda del header (light tint del primary) */
export const headerSearchBoxSx = {
  position: 'relative' as const,
  borderRadius: 2,
  backgroundColor: 'rgba(19, 103, 138, 0.08)',
  '&:hover': {
    backgroundColor: 'rgba(19, 103, 138, 0.12)',
  },
  transition: 'background-color 0.2s ease',
  display: 'flex',
  alignItems: 'center',
  px: 2,
  py: 0.5,
};

/** InputBase dentro del search box del header */
export const headerSearchInputSx = {
  ml: 1,
  flex: 1,
  color: 'text.primary',
  '& .MuiInputBase-input': {
    padding: '8px 0',
  },
};

/** Dropdown de resultados de búsqueda */
export const headerSearchDropdownSx = {
  position: 'absolute' as const,
  top: '100%',
  left: 0,
  right: 0,
  zIndex: 1300,
  mt: 1,
  maxHeight: 400,
  overflow: 'auto',
  borderRadius: 2,
  boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
};
